import * as React from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import * as Yup from 'yup';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { Autocomplete, Radios, TextField } from 'mui-rff';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { Button, Divider, Grid, Typography } from '@material-ui/core';
import { useStyles } from './css';
import { FormPage } from '../../../components/FormPage';
import { FormSection } from '../../../components/FormSection';
import { ImageUploadInput } from '../../../components/ImageUploadInput';
import { FormCondition } from '../../../components/FormCondition';
import Box from '@material-ui/core/Box';

export function ListingFormView({
    app,
    listing,
    loading,
    onSubmit,
    deleting,
    onDelete,
    onCancel,
    onDeleteImage,
    deletingImage,
    s3UploadRequestMutation,
}: any) {
    const classes = useStyles();
    if (app === undefined || listing === undefined) {
        return <CircularProgress />;
    }

    const breadcrumbs = [
        { label: 'Apps', link: '/extranet/apps' },
        { label: 'Dashboard', link: '/extranet/apps/' + app.id + '/dashboard' },
    ];

    const screenshotTypes = [
        { value: 'DESKTOP', label: 'Desktop' },
        { value: 'MOBILE', label: 'Mobile' },
    ];
    const emptyScreenshotObject = {
        description: '',
        type: 'DESKTOP',
    };
    const emptyKeyBenefitObject = {
        description: '',
        title: '',
    };

    const getUploadParams = async ({ file, meta }) => {
        const { data } = await s3UploadRequestMutation({
            variables: {
                contentType: file.type,
            },
        });
        return {
            body: file,
            method: 'PUT',
            url: data.s3UploadAuthorization.node.uploadUrl,
            meta: { ...meta, ...data.s3UploadAuthorization.node.meta },
        };
    };

    const getPricePlanDetails = formValues => {
        const details = [];
        for (const plan of app.pricePlans) {
            // eslint-disable-next-line eqeqeq
            const planDesc = formValues.find(v => v.planId == plan.id);
            if (planDesc === undefined) {
                details.push({
                    planId: plan.id,
                    name: plan.name,
                    features: '',
                });
            } else {
                details.push({
                    planId: planDesc.planId,
                    name: planDesc.name,
                    features: planDesc.features,
                });
            }
        }
        return details;
    };

    listing.pricePlans = getPricePlanDetails(listing.pricePlans);

    const validationSchema = Yup.lazy(() => {
        return Yup.object({
            name: Yup.string().required('Name is required').max(30, 'Max 30 chars'),
            tagLine: Yup.string().required('Tagline is required').max(80, 'Max 80 chars'),
            description: Yup.string()
                .required('Detailed description is required')
                .max(4000, 'Max 4000 chars'),
            searchTerms: Yup.array().max(5, 'Max 5 terms.'),
            integrations: Yup.array().max(6, 'Max 6 integrations.'),
            includeKeyBenefitsImages: Yup.boolean(),
            icon: Yup.object().required('App icon is required').nullable(),
            keyBenefits: Yup.array()
                .of(
                    Yup.object({
                        title: Yup.string()
                            .required('Key Benefit title is required')
                            .max(30, 'Max 30 chars'),
                        description: Yup.string()
                            .required('Key Benefit description is required')
                            .max(140, 'Max 140 chars'),
                        //image: values.includeKeyBenefitsImages ? Yup.object().required("Key Benefit image is required").nullable() : Yup.object().nullable()
                    })
                )
                .max(3, 'Max 3 key benefits.'),
            screenShots: Yup.array().of(
                Yup.object({
                    description: Yup.string()
                        .required('Screenshot description is required')
                        .max(64, 'Max 64 chars'),
                    image: Yup.object().required('Screenshot image is required').nullable(),
                })
            ),
            /*
            pricePlans: Yup.array().of(Yup.object({
                name: Yup.string().required("Price plan name is required").max(20, "Max 20 chars"),
                features: Yup.string().required("Price plan features is required").max(140, "Max 140 chars"),
            })),
             */
            supportEmail: Yup.string()
                .required('Support email is required')
                .email('Support email is not a valid address'),
            websiteUrl: Yup.string().url('Not a valid URL'),
            privacyPolicyUrl: Yup.string().url('Not a valid URL'),
            termsOfServiceUrl: Yup.string().url('Not a valid URL'),
        });
    });

    const SwitchAdapter = ({ input: { onChange, value }, label, ...rest }: any) => (
        <FormControlLabel
            control={<Switch checked={!!value} onChange={onChange} color="primary" {...rest} />}
            label={label}
        />
    );

    return (
        <FormPage
            title={listing.id ? 'Edit App Listing' : 'Create App Listing'}
            type={'create'}
            breadcrumbs={breadcrumbs}
            initialValues={listing}
            validationSchema={validationSchema}
            loading={loading}
            onCancel={onCancel}
            onDelete={onDelete}
            deleting={deleting}
            onSubmit={onSubmit}
            previewComponent={<div>test</div>}
            render={({
                form: {
                    mutators: { push },
                },
            }) => (
                <React.Fragment>
                    <FormSection
                        title="Listing Information"
                        description="Provide a customer-facing name and tagline for your app. A tagline is a one-line advertisement for your app that will appear in search results, recommendations, and other contexts. It should be clear from the tagline what the app does and how it benefits the vendor."
                    >
                        <TextField
                            name="name"
                            required
                            fullWidth
                            label="Name"
                            margin="normal"
                            autoFocus
                        />
                        <TextField
                            name="tagLine"
                            required
                            fullWidth
                            label="Tagline"
                            margin="normal"
                        />
                    </FormSection>

                    <FormSection
                        title="App icon"
                        description="Your app icon helps users visually recognize your app in search results and other contexts. Dimensions: 1200px by 1200px."
                    >
                        <Field
                            name="icon"
                            label="Add image"
                            component={ImageUploadInput}
                            getUploadParams={getUploadParams}
                            multiple={false}
                            maxFiles={1}
                            onDelete={(s3Key, callback) => onDeleteImage('ICON', s3Key, callback)}
                            deleting={deletingImage}
                        />
                    </FormSection>

                    {app.type === 'PUBLIC' && (
                        <FormSection
                            title="Search terms (optional)"
                            description="Make it easier for vendors to search for your app by including tags that fit your app description. Press the enter key to add a term. (Maximum 5)"
                        >
                            <Field name="searchTerms" subscription={{ value: true }}>
                                {({ input: { value } }) => (
                                    <Autocomplete
                                        name="searchTerms"
                                        label="Search terms"
                                        textFieldProps={{ margin: 'normal' }}
                                        options={value}
                                        freeSolo
                                        multiple
                                    />
                                )}
                            </Field>
                        </FormSection>
                    )}

                    <FormSection
                        title="Promotional video (optional)"
                        description="Link to YouTube or Vimeo video to market the app’s feature and benefits and motivate vendors to install it. Don’t include a screencast. Save those for on-boarding or tutorials. Recommended length: 2 mins or less."
                    >
                        <TextField
                            name="promoVideoUrl"
                            fullWidth
                            label="Youtube or Vimeo link"
                            margin="normal"
                        />
                    </FormSection>

                    {app.type === 'PUBLIC' && (
                        <FormSection
                            title="Key benefits"
                            description="Highlight up to three benefits of your app to persuade vendor that it is a must-have tool. How does it help the vendor save time or make money? Be sure to describe benefits and not features. Image dimensions: 1600px by 1200px"
                        >
                            <Field
                                component={SwitchAdapter}
                                name="includeKeyBenefitsImages"
                                label="Include images (optional)"
                            />

                            <FieldArray name="keyBenefits">
                                {({ fields }) =>
                                    fields.map((name, index) => (
                                        <div key={name}>
                                            <Typography
                                                variant="subtitle2"
                                                className={classes.fieldArrayItemHeading}
                                            >
                                                Key Benefit {index + 1}
                                            </Typography>
                                            <TextField
                                                name={`${name}.title`}
                                                label="Title"
                                                required={true}
                                                margin="normal"
                                            />
                                            <TextField
                                                name={`${name}.description`}
                                                label="Description"
                                                multiline
                                                rowsMax="5"
                                                margin="normal"
                                                required={true}
                                            />

                                            <FormCondition
                                                when="includeKeyBenefitsImages"
                                                is={true}
                                            >
                                                <Field
                                                    name={`${name}.image`}
                                                    label="Add image"
                                                    component={ImageUploadInput}
                                                    getUploadParams={getUploadParams}
                                                    multiple={false}
                                                    maxFiles={1}
                                                    onDelete={(s3Key, callback) =>
                                                        onDeleteImage(
                                                            'KEY_BENEFIT',
                                                            s3Key,
                                                            callback
                                                        )
                                                    }
                                                    deleting={deletingImage}
                                                />
                                            </FormCondition>

                                            <Button
                                                type="button"
                                                size="small"
                                                variant="outlined"
                                                color="secondary"
                                                className={classes.fieldArrayButton}
                                                onClick={() => fields.remove(index)}
                                            >
                                                Delete Key Benefit
                                            </Button>
                                            <Divider className={classes.fieldArrayDivider} />
                                        </div>
                                    ))
                                }
                            </FieldArray>

                            <FormCondition when="keyBenefits" is={value => value.length < 3}>
                                <Button
                                    type="button"
                                    size="small"
                                    variant="outlined"
                                    className={classes.fieldArrayButton}
                                    onClick={() => push('keyBenefits', emptyKeyBenefitObject)}
                                >
                                    + Add key benefit
                                </Button>
                            </FormCondition>
                        </FormSection>
                    )}

                    <FormSection
                        title="Screenshots"
                        description="Provide a collection of clear and well-cropped screenshots of your app to help vendors understand how your app works and how it can help them. Provide alt text for your images for accessibility and to help with SEO."
                    >
                        <FieldArray name="screenShots">
                            {({ fields }) =>
                                fields.map((name, index) => (
                                    <div key={name}>
                                        <Typography
                                            variant="subtitle2"
                                            className={classes.fieldArrayItemHeading}
                                        >
                                            Screenshot {index + 1}
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item md={6} xs={12}>
                                                <TextField
                                                    name={`${name}.description`}
                                                    label="Description"
                                                    margin="normal"
                                                    required={true}
                                                />
                                                <div>
                                                    <Radios
                                                        label="What device?"
                                                        name={`${name}.type`}
                                                        data={screenshotTypes}
                                                        formControlProps={{
                                                            margin: 'normal',
                                                        }}
                                                    />
                                                </div>
                                                <Button
                                                    type="button"
                                                    size="small"
                                                    variant="outlined"
                                                    color="secondary"
                                                    className={classes.fieldArrayButton}
                                                    onClick={() => fields.remove(index)}
                                                >
                                                    Delete Screenshot
                                                </Button>
                                            </Grid>
                                            <Grid item md={6} xs={12}>
                                                <Field
                                                    component={ImageUploadInput}
                                                    name={`${name}.image`}
                                                    getUploadParams={getUploadParams}
                                                    multiple={false}
                                                    maxFiles={1}
                                                    text="Add image"
                                                    onDelete={(s3Key, callback) =>
                                                        onDeleteImage('SCREENSHOT', s3Key, callback)
                                                    }
                                                    deleting={deletingImage}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Divider className={classes.fieldArrayDivider} />
                                    </div>
                                ))
                            }
                        </FieldArray>

                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            className={classes.fieldArrayButton}
                            onClick={() => push('screenShots', emptyScreenshotObject)}
                        >
                            + Add screenshot
                        </Button>
                    </FormSection>

                    <FormSection
                        title="Detailed description"
                        description="Provide details about how the app can make vendors more successful. This is where you can list app features and additional selling points, and describe how the app works."
                    >
                        <TextField
                            name="description"
                            fullWidth
                            label="Description"
                            multiline
                            required={true}
                            rows={2}
                            rowsMax="10"
                            margin="normal"
                        />
                    </FormSection>

                    {app.type === 'PUBLIC' && (
                        <FormSection
                            title="Integrations (optional)"
                            description="Does your app integrate with any other services or devices? List them here. Press the enter key to add. (Maximum 6)"
                        >
                            <Field name="integrations" subscription={{ value: true }}>
                                {({ input: { value } }) => (
                                    <Autocomplete
                                        name="integrations"
                                        label="Integrations"
                                        textFieldProps={{ margin: 'normal' }}
                                        options={value}
                                        freeSolo
                                        multiple
                                    />
                                )}
                            </Field>
                        </FormSection>
                    )}

                    {app.chargeType === 'RECURRING' && (
                        <FormSection
                            title="Pricing details"
                            description="Describe the price plans, and the features included in each plan."
                        >
                            <FieldArray name="pricePlans">
                                {({ fields }) =>
                                    fields.map(name => (
                                        <div key={name}>
                                            <TextField
                                                name={`${name}.name`}
                                                label="Name"
                                                required
                                                margin="normal"
                                            />
                                            <TextField
                                                name={`${name}.features`}
                                                label="Features"
                                                multiline
                                                rowsMax="5"
                                                margin="normal"
                                            />

                                            <Divider className={classes.fieldArrayDivider} />
                                        </div>
                                    ))
                                }
                            </FieldArray>
                        </FormSection>
                    )}

                    <FormSection
                        title="Sales and Support"
                        description="Provide at least one support contact method, either phone, email or a website URL. If possible, provide all three. A contact phone number can boost vendor confidence by reassuring them that there's a real person behind the app."
                    >
                        <TextField
                            type="email"
                            name="supportEmail"
                            placeholder="support@example.com"
                            fullWidth
                            required
                            label="Support email"
                            margin="normal"
                        />
                        <TextField
                            type="text"
                            name="supportPhone"
                            fullWidth
                            label="Support phone"
                            margin="normal"
                        />
                        <TextField
                            name="websiteUrl"
                            placeholder="https://example.com/myapp"
                            fullWidth
                            label="Website URL"
                            margin="normal"
                        />
                        <Box mt={2}>
                            <Field
                                component={SwitchAdapter}
                                name="showContactDetails"
                                label="Show contact details"
                            />
                            <Typography>
                                <small>
                                    If enabled, the customer can see your support email and support
                                    phone number if supplied.
                                </small>
                            </Typography>
                        </Box>
                        <Box mt={2}>
                            <Field
                                component={SwitchAdapter}
                                name="offerDemoRequest"
                                label="Customers can request a demo of this app"
                            />
                            <Typography>
                                <small>
                                    If enabled, the customer can request a demo of your app. You
                                    will receive their contact details to your registered provided
                                    support email.
                                </small>
                            </Typography>
                        </Box>
                    </FormSection>

                    <FormSection
                        title="Terms & Privacy Policy"
                        description="To protect your app, company, and customers, you need to state your terms of use in clear, simple, and easily understood language. If your app deals with customer data, we strongly encourage you to have your privacy policy documented on your website. Provide a URL so vendors can find out what steps you're taking to protect their data."
                    >
                        <TextField
                            name="termsOfServiceUrl"
                            placeholder="https://example.com/terms"
                            fullWidth
                            label="Terms of Service URL"
                            margin="normal"
                        />

                        <TextField
                            name="privacyPolicyUrl"
                            placeholder="https://example.com/privacy"
                            fullWidth
                            label="Privacy Policy URL"
                            margin="normal"
                        />
                    </FormSection>
                </React.Fragment>
            )}
        />
    );
}
